<template>
  <div>
    <PageDynamicBreadcrumb />

    <div class="row items-center justify-start q-mb-lg">
      <DateRangeSelector
        :initial-start-date="startDate"
        :initial-end-date="endDate"
        @update="
          dates => {
            updateDateRange(dates);
            getLeads();
          }
        "
        url-state
      />
    </div>

    <DataTable
      table-key="electricityRatesPii"
      row-key="id"
      title="Electricity Rates PII"
      :columns="columns"
      :rows="leads"
      :loading="loading"
      :pagination="{
        sortBy: 'time_created',
        descending: true,
        page: 1,
        rowsPerPage: 25
      }"
      :downloadable="true"
      :refreshable="true"
      :customizable="false"
      :filterable="true"
      :searchable="true"
      @update-rows="getLeads()"
    >
      <template v-slot:actions="row">
        <ActionsDropdownButton
          :actions="[
            {
              action: () => promptRemovePii(row),
              label: 'Delete PII'
            }
          ]"
        />
      </template>
    </DataTable>
  </div>
</template>

<script>
import PageDynamicBreadcrumb from "@/components/UI/PageDynamicBreadcrumb";
import axios from "axios";
import moment from "moment/moment";
import DataTable from "@/components/DataTable";
import DateRangeSelector from "@/components/UI/DateRangeSelector";
import ActionsDropdownButton from "@/components/UI/ActionsDropdownButton.vue";
import ConfirmationModalDelete from "@/components/UI/ConfirmationModalDelete.vue";
import {Dialog} from "quasar";


export default {
  name: "ElectricityRatesPiiReport",
  components: {
    ActionsDropdownButton,
    DateRangeSelector,
    DataTable,
    PageDynamicBreadcrumb
  },
  data() {
    return {
      loading: true,
      startDate: this.$route.query.start_date
        ? this.$route.query.start_date
        : moment().format("YYYY-MM-DD"),
      endDate: this.$route.query.end_date
        ? this.$route.query.end_date
        : moment().format("YYYY-MM-DD"),
      datePreset: this.$route.query.date_preset
        ? this.$route.query.date_preset
        : "",
      pagination: {
        sortBy: "id",
        descending: true,
        rowsPerPage: 25
      },
      totals: {
        total: 0,
        pending: 0,
        successful: 0,
        rejected: 0
      },
      leads: [],
      pendingGetLeadsRequests: [],
      columns: [
        {
          name: "visitor_id",
          label: "Visitor ID",
          field: "visitor_id",
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b),
          type: "dimension",
          filterable: false
        },
        {
          name: "time_created",
          label: "Time Created",
          field: row => row.time_created,
          format: val =>
            !val ? "-" : moment(val).format("MMMM D, YYYY h:mma"),
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "first_name",
          label: "First Name",
          field: "first_name",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "last_name",
          label: "Last Name",
          field: "last_name",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "phone",
          label: "Phone",
          field: "phone",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "email",
          label: "Email",
          field: "email",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "postal_code",
          label: "Postal Code",
          field: "postal_code",
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b),
          type: "dimension",
          filterable: false
        },
        {
          name: "total_purchase_events",
          label: "Total Purchases",
          field: "total_purchase_events",
          sortable: true,
          type: "dimension",
          filterable: true
        },
        {
          name: "successful_purchase_events",
          label: "Successful Purchases",
          field: "successful_purchase_events",
          sortable: true,
          type: "dimension",
          filterable: true
        },
        {
          name: "ip_address",
          label: "IP Address",
          field: "ip_address",
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b),
          type: "dimension",
          filterable: false
        },
        {
          name: "user_agent",
          label: "User Agent",
          field: "user_agent",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "actions",
          label: "",
          field: "",
          filterable: false
        }
      ]
    };
  },
  beforeMount() {
    if (this.datePreset.length > 0) {
      let dateRange = this.getDatePresetRange(this.datePreset);
      this.startDate = dateRange.from;
      this.endDate = dateRange.to;
    }
  },
  mounted() {
    this.getLeads();
  },
  methods: {
    updateDateRange(dates = null) {
      if (dates) {
        this.startDate = dates.startDate;
        this.endDate = dates.endDate;
      }
    },
    getLeads() {
      this.loading = true;

      // Abort any existing requests
      if (this.pendingGetLeadsRequests.length > 0) {
        this.pendingGetLeadsRequests.forEach(controller => {
          controller.abort();
        });
        this.pendingGetLeadsRequests = [];
      }
      let controller = new AbortController();
      let { signal } = controller;
      this.pendingGetLeadsRequests.push(controller);

      this.leads = [];

      axios({
        url: "https://api-v2.jumpfeed.com/internal/electricity-rates-pii-report",
        params: {
          start_date: this.startDate,
          end_date: this.endDate
        },
        method: "GET",
        signal: signal
      })
        .then(response => {
          this.leads = response.data.data.rows;
          this.totals = 0;

          this.loading = false;
        })
        .catch(err => {
          if (axios.isCancel(err)) {
            return;
          }
          this.loading = false;
        });
    },
    promptRemovePii(row) {
      Dialog.create({
        component: ConfirmationModalDelete,
        componentProps: {
          descriptor: row.first_name + " " + row.last_name,
        }
      }).onOk(dialog => {
        dialog.submitting = true;
        axios({
          url:
            "https://api-v2.jumpfeed.com/internal/electricity-rates-pii-report/remove",
          params: {
            visitor_id: row.visitor_id
          },
          method: "POST"
        })
          .then(() => {
            this.triggerActionOutcomeAlert("delete", true);
            this.getLeads();
          })
          .catch(e => {
            this.triggerActionOutcomeAlert("delete", false);
            console.log("Error removing PII record:", e);
          })
          .finally(() => {
            dialog.submitting = false;
            dialog.hide();
          });
      });
    }
  }
};
</script>

<style scoped lang="scss"></style>
